import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchProjectParityRuns } from '../../lib/api/parity';
import { StatusBadge, type StatusTone } from '../ui';

/**
 * FORGE-348 P1 — project-level Parity reports history (Activity → Parity
 * reports). Every Before baseline and After verification across the
 * project's repos, newest first, with the report ID, hash and PDF link.
 */
const TONE: Record<string, StatusTone> = {
  READY: 'success', READY_WITH_GAPS: 'warning', PASS: 'success', PASS_WITH_EXCEPTIONS: 'warning', FAIL: 'danger',
};

export const ParityRunsTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const query = useQuery({ queryKey: ['project-parity-runs', projectId], queryFn: () => fetchProjectParityRuns(projectId), retry: false });
  if (query.isLoading) return <div className="text-sm text-slate-500">Loading parity reports…</div>;
  if (query.isError) return <div role="alert" className="text-sm text-danger-700">{(query.error as Error).message}</div>;
  const runs = query.data ?? [];
  if (runs.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-600" data-testid="parity-runs-empty">
        No Parity Proof runs yet. Comment <code>/parity-before</code> on an epic issue to prove, before any change, that Forge
        understands the system and can test it — then <code>/parity-after</code> to prove the migration changed nothing.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="parity-runs">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr><th className="px-3 py-2">Date</th><th>Repo</th><th>Epic</th><th>Phase</th><th>Verdict</th><th>Report</th></tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.runId} className="border-t border-slate-100" data-testid="parity-run-row">
              <td className="px-3 py-2 tabular-nums text-slate-600">{r.createdAt ? r.createdAt.slice(0, 10) : '—'}</td>
              <td className="font-mono text-xs">{r.repo}</td>
              <td className="tabular-nums">{r.epic ?? '—'}</td>
              <td>{r.phase === 'before' ? 'Before' : 'After'}</td>
              <td>{r.verdict ? <StatusBadge tone={TONE[r.verdict] ?? 'neutral'}>{r.verdict.replace(/_/g, ' ')}</StatusBadge> : <span className="text-xs text-slate-500">{r.state.replace(/_/g, ' ').toLowerCase()}</span>}</td>
              <td className="text-xs">
                {r.pdfUrl
                  ? <a className="font-medium text-primary-600 hover:underline" href={r.pdfUrl} target="_blank" rel="noreferrer">PDF</a>
                  : <span className="text-slate-400">—</span>}
                {r.reportId && <span className="ml-2 font-mono text-slate-500" title={r.reportHash ?? ''}>{r.reportId}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
